/**
 * Выбор роли: сразу после авторизации, до входа в приложение (для пользователей с доступом к админке).
 * standalone = без Layout/таббара, полноэкранный экран-шлюз.
 */

import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth.tsx";
import { IconShield, IconUser } from "../../components/Icons.tsx";

const ROLE_CHOSEN_KEY = "onechurch_role_chosen";

export function setRoleChosen(value: "admin" | "profile"): void {
  try {
    sessionStorage.setItem(ROLE_CHOSEN_KEY, value);
  } catch {
    // ignore
  }
}

export function getRoleChosen(): "admin" | "profile" | null {
  try {
    const v = sessionStorage.getItem(ROLE_CHOSEN_KEY);
    if (v === "admin" || v === "profile") return v;
    return null;
  } catch {
    return null;
  }
}

export function clearRoleChosen(): void {
  try {
    sessionStorage.removeItem(ROLE_CHOSEN_KEY);
  } catch {
    // ignore
  }
}

function ChoiceCard({
  profileTo,
  adminTo,
  onChoose,
}: {
  profileTo: string;
  adminTo: string;
  onChoose: (role: "admin" | "profile") => void;
}) {
  return (
    <div className="card p-4 xs:p-5 md-mobile:p-6 w-full max-w-sm">
      <h2 className="text-base xs:text-lg md-mobile:text-xl font-semibold text-slate-900 text-center mb-4">
        Куда перейти?
      </h2>
      <div className="flex flex-col gap-4">
        <Link
          to={profileTo}
          replace
          onClick={() => onChoose("profile")}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] no-underline"
          style={{
            background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
          }}
        >
          <IconUser className="w-5 h-5 xs:w-6 xs:h-6" />
          Личный кабинет
        </Link>
        <Link
          to={adminTo}
          replace
          onClick={() => onChoose("admin")}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm text-slate-700 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98] transition-all no-underline"
        >
          <IconShield className="w-5 h-5" />
          В админку
        </Link>
      </div>
    </div>
  );
}

interface ChooseRolePageProps {
  /** Режим «шлюз»: без Layout, сразу после авторизации, до входа в приложение */
  standalone?: boolean;
  /** Вызывается при выборе роли — обновляет state в AuthGuard, чтобы экран переключился даже при том же URL */
  onChoose?: (role: "admin" | "profile") => void;
}

export function ChooseRolePage({
  standalone = false,
  onChoose: onChooseFromParent,
}: ChooseRolePageProps) {
  const navigate = useNavigate();
  const { account } = useAuth();

  const canAccessAdmin = account?.canAccessAdmin === true;

  const handleChoose = (role: "admin" | "profile") => {
    if (onChooseFromParent) onChooseFromParent(role);
    else setRoleChosen(role);
  };

  if (!canAccessAdmin) {
    if (!standalone) navigate("/", { replace: true });
    return null;
  }

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center p-3 xs:p-4 md-mobile:p-5 page-container relative z-20 safe-area-top safe-area-bottom"
      aria-label="Выбор: личный кабинет или админка"
    >
      <div className="text-center mb-5 xs:mb-6 md-mobile:mb-8">
        <img
          src="/logo.png"
          alt=""
          className="w-14 h-14 xs:w-16 xs:h-16 md-mobile:w-20 md-mobile:h-20 mx-auto mb-3 xs:mb-4 rounded-xl object-contain"
        />
        <h1 className="text-lg xs:text-xl md-mobile:text-2xl font-bold text-slate-900 mb-1">
          OneChurch
        </h1>
        <p className="text-sm xs:text-base text-slate-500">
          Евангельская церковь на Пхукете
        </p>
      </div>
      <ChoiceCard profileTo="/" adminTo="/admin" onChoose={handleChoose} />
    </div>
  );
}
